import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import { 
  Home as HomeIcon, 
  Building, 
  Key, 
  Sparkles, 
  Wind, 
  HardHat, 
  Heart,
  CheckCircle2
} from 'lucide-react';
import { services } from '../data/mock';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const ServicesPage = () => {
  const iconMap = {
    'houses': HomeIcon,
    'apartments': Building,
    'vacation-homes': Key,
    'small-offices': Building,
    'retail-locations': Building,
    'deep-cleaning': Sparkles,
    'move-cleaning': Key,
    'window-cleaning': Wind,
    'post-construction': HardHat,
    'senior-checkin': Heart
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "name": "Cleaning Services | Pierce Premier Cleaning Services",
        "description": "Professional residential and commercial cleaning services in Brevard County, Florida",
        "breadcrumb": {
          "@id": "https://pierce-premier.preview.emergentagent.com/services#breadcrumb"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://pierce-premier.preview.emergentagent.com/services#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://pierce-premier.preview.emergentagent.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Services"
          }
        ]
      },
      {
        "@type": "Service",
        "serviceType": "House Cleaning",
        "provider": {
          "@type": "LocalBusiness",
          "name": "Pierce Premier Cleaning Services"
        },
        "areaServed": {
          "@type": "City",
          "name": "Brevard County, Florida"
        }
      }
    ]
  };

  return (
    <>
      <SEO 
        title="Cleaning Services | Pierce Premier | Brevard County, FL"
        description="Professional house cleaning, deep cleaning, move-in/out, commercial cleaning, and more in Brevard County. Locally owned with 15+ years experience."
        canonical="/services"
        structuredData={structuredData}
      />
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-cyan-50 via-blue-50 to-purple-50 py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-7xl text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Our Services
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Professional cleaning solutions for every need. From residential to commercial, we've got you covered.
          </p>
        </div>
      </section>

      {/* Residential Services */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Residential Cleaning
            </h2>
            <p className="text-lg text-gray-600">
              Keep your home fresh, clean, and welcoming
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.residential.map((service) => {
              const Icon = iconMap[service.id];
              return (
                <Card key={service.id} className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-cyan-200">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {service.name}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {service.description}
                    </p>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-sm text-gray-600">
                          <CheckCircle2 className="w-4 h-4 text-cyan-600 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Commercial Services */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Commercial Cleaning
            </h2>
            <p className="text-lg text-gray-600">
              Professional cleaning for your business space
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.commercial.map((service) => {
              const Icon = iconMap[service.id];
              return (
                <Card key={service.id} className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {service.name}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {service.description}
                    </p>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-sm text-gray-600">
                          <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Specialized Services */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Specialized Cleaning
            </h2>
            <p className="text-lg text-gray-600">
              Expert cleaning for unique situations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.specialized.map((service) => {
              const Icon = iconMap[service.id];
              return (
                <Card key={service.id} className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-purple-200">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-400 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {service.name}
                    </h3>
                    <p className="text-gray-600 mb-4 text-sm">
                      {service.description}
                    </p>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-xs text-gray-600">
                          <CheckCircle2 className="w-3 h-3 text-purple-600 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Senior Care Service - Highlighted */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-pink-50 via-rose-50 to-purple-50">
        <div className="container mx-auto max-w-7xl">
          <div className="mb-12 text-center">
            <Badge className="bg-rose-500 text-white mb-4">Special Service</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Discreet Home Check-In Service
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A thoughtful service for families who want an extra set of trusted eyes
            </p>
          </div>

          {services.additional.map((service) => (
            <Card key={service.id} className="max-w-4xl mx-auto border-4 border-rose-200 shadow-2xl">
              <CardContent className="p-8 md:p-12">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <div className="w-20 h-20 bg-gradient-to-br from-pink-400 to-rose-500 rounded-2xl flex items-center justify-center mb-6">
                      <Heart className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">
                      {service.name}
                    </h3>
                    <p className="text-lg text-gray-600 mb-6">
                      {service.description}
                    </p>
                    {service.disclaimer && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-700 font-medium">
                          <span className="font-semibold">Important:</span> {service.disclaimer}
                        </p>
                      </div>
                    )}
                  </div>
                  <div>
                    <ul className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <CheckCircle2 className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 font-medium">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-cyan-600 to-blue-700 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-cyan-50">
            Contact us today for a free quote on any of our services
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-cyan-600 hover:bg-gray-100 text-lg px-8 py-6">
              Request Free Quote
            </Button>
          </Link>
        </div>
      </section>
    </div>
    </>
  );
};

export default ServicesPage;
